import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Input, Icon, Button } from 'semantic-ui-react';
import TailwindPeriodInput from 'widgets/form/period-input-tw';
import { DateTimePeriod, EventSupportPositionsDto, EventPositionDto, TravelRangeDto } from 'stores/events/events-store';
import { UserDto } from 'stores/users/users-store';
import ChooseStudentsForm from 'widgets/form/choose-students-form';
import { DatePeriod } from 'site/pages/landing-pages/shared-scheduler-components/events/scheduler-period-service';
import { ReducerType, useStateReducer } from 'utils/react-custom-hooks';
import { IdentityService } from 'services/identity-service';
import { container } from 'inversify.config';
import { DatesRangeInput } from 'widgets/form/datesRangeInput';
import { EventFormViewModel } from 'stores/events/event-form-store';

interface TailwindInfoSupportDetailedProps extends WithTranslation, EventSupportPositionsDto {
  readonly: boolean;
  onChange: (values: EventSupportPositionsDto) => void;
  onNewPosition: (newUser: UserDto, dates: DatePeriod) => void;
  handleTravelDays: (property: keyof TravelRangeDto, { from, to }: DateTimePeriod, index: number) => void;
  isHideEditButton: boolean;
  //eventFormStore?: EventFormStore;
  event: EventFormViewModel;
}

interface TailwindInfoSupportDetailedState {
  showEmployeeSelectionModal: boolean;
  newPosition: boolean;
  userChangeIndex: number;
}

const getDefaultValues = (): TailwindInfoSupportDetailedState => ({
  showEmployeeSelectionModal: false,
  newPosition: false,
  userChangeIndex: -1
});

const TailwindInfoSupportDetailed: React.FC<TailwindInfoSupportDetailedProps> = props => {
  const identityService: IdentityService = container.get(IdentityService);
  const isAdminPocOrPlanner = (): boolean => {
    return IdentityService.isAdminPocOrPlanner(identityService.getUserInfo());
  };
  const isInstructor = (): boolean => {
    return IdentityService.isInstructor(identityService.getUserInfo());
  };
  const {
    positionCodeId,
    positionDescription,
    requestEndDate,
    requestStartDate,
    t,
    onChange,
    onNewPosition,
    handleTravelDays,
    // eventFormStore,
    event,
    ...rest
  } = props;
  const { positionCodeName, comment, positionsRequested, eventPositions, id, readonly, isHideEditButton } = rest;
  const [{ showEmployeeSelectionModal, newPosition, userChangeIndex }, setState] = React.useReducer<
    ReducerType<TailwindInfoSupportDetailedState>,
    TailwindInfoSupportDetailedState
  >(useStateReducer, getDefaultValues(), getDefaultValues);

  const initialItem = {
    id,
    positionCodeId,
    positionCodeName,
    positionDescription,
    comment,
    positionsRequested,
    eventPositions,
    requestEndDate,
    requestStartDate
  };
  const openModal = (isNewPosition: boolean = false) => {
    const changes: Partial<TailwindInfoSupportDetailedState> = { showEmployeeSelectionModal: true };
    if (isNewPosition) changes.newPosition = isNewPosition;
    setState(changes);
  };

  const closeModal = () => setState({ showEmployeeSelectionModal: false });

  const onEmployeesSelected = (employees: UserDto[]) => {
    const employee = employees.firstOrDefault();
    if (employee == null) return;
    if (newPosition) onNewPosition(employee, { from: requestStartDate, to: requestEndDate });
    else {
      const eventPositions = props.eventPositions.map((eventPosition, i) =>
        userChangeIndex === i
          ? {
              ...eventPosition,
              userLocationId: employee.roles.find(({ role }) => role.name === 'Employee').location.id,
              userId: employee.id,
              user: employee
            }
          : eventPosition
      );
      onChange({ ...initialItem, eventPositions });
    }

    setState({ newPosition: false, userChangeIndex: -1 });
  };

  const changeEmployeeHandler = (idx: number) => setState({ userChangeIndex: idx, showEmployeeSelectionModal: true });

  // const formatInput = (e: any) => {
  //   let checkIfNum;
  //   if (e.key !== undefined) {
  //     // Check if it's a "e", ".", "+" or "-"
  //     checkIfNum = e.key === 'e' || e.key === '.' || e.key === '+' || e.key === '-';
  //   } else if (e.keyCode !== undefined) {
  //     // Check if it's a "e" (69), "." (190), "+" (187) or "-" (189)
  //     checkIfNum = e.keyCode === 69 || e.keyCode === 190 || e.keyCode === 187 || e.keyCode === 189;
  //   }
  //   return checkIfNum && e.preventDefault();
  // };

  const datesChangeHandler = ({ from, to }: DateTimePeriod, idx: number, travelDays: boolean = false) => {
    let eventPositions: EventPositionDto[];
    const fromDate = travelDays ? 'travelDateFrom' : 'startDate';
    const toDate = travelDays ? 'travelDateTo' : 'endDate';

    if (from !== props.eventPositions[idx][fromDate])
      eventPositions = props.eventPositions.map((supportPos, i) => (i === idx ? { ...supportPos, [fromDate]: from } : supportPos));
    else if (to !== props.eventPositions[idx][toDate])
      eventPositions = props.eventPositions.map((supportPos, i) => (i === idx ? { ...supportPos, [toDate]: to } : supportPos));

    eventPositions && onChange({ ...initialItem, eventPositions });
  };

  const inputChangeHandler = (property: string, value: any, idx: number) => {
    let eventPositions;
    eventPositions = props.eventPositions.map((supportPos, i) => (i === idx ? { ...supportPos, [property]: value } : supportPos));
    eventPositions && onChange({ ...initialItem, eventPositions });
  };

  const handleNoTravel = (index: number, value: boolean) => {
    inputChangeHandler('noTravel', value, index);
  };

  let emptyPositions = [];
  if ((eventPositions || []).length < positionsRequested) {
    for (let index = 0; index < positionsRequested - eventPositions.length; index++) {
      emptyPositions.push(
        <div className="events-support-details__info-position">
          <label>Position</label>
          <Input disabled value={positionDescription} />
          <Button disabled={readonly} onClick={() => openModal(true)} icon>
            <Icon name="user plus" />
          </Button>
        </div>
      );
    }
  }

  //const { status } = eventFormStore.state.item;
  const { status } = event;

  return (
    <>
      {(eventPositions || []).map((ep, idx) => {
        const { actualCost, actualHours, actualDays, endDate, startDate, travelDays, noTravel, user, userId } = ep;
        const employeeRole = user.roles.find(x => x.role.name === 'Employee');
        const employeeLocation = employeeRole?.location?.location || t('|LOCATION|');
        return (
          <div key={'event-position' + id + comment + userId + idx} className="bg-gray-50 rounded shadow p-4 mb-4 flex flex-col gap-4">
            <div>
              <label className="text-sm text-gray-700">{t('Free text from Request')}</label>
              <input className="w-full border border-gray-300 p-2 rounded bg-gray-100" value={comment} disabled />
            </div>

            <div>
              <label className="text-sm text-gray-700">{t('Position')}</label>
              <input className="w-full border border-gray-300 p-2 rounded bg-gray-100" value={positionDescription} disabled />
            </div>

            <div>
              <label className="text-sm text-gray-700">{t('Employee')}</label>
              <input
                className="w-full border border-gray-300 p-2 rounded bg-gray-100"
                value={user.lastName + ', ' + user.firstName}
                disabled
              />
            </div>

            <div>
              <label className="text-sm text-gray-700">{t('SAP ID')}</label>
              <input className="w-full border border-gray-300 p-2 rounded bg-gray-100" value={user.employeeId} disabled />
            </div>

            <div>
              <label className="text-sm text-gray-700">{t('Position Code')}</label>
              <input className="w-full border border-gray-300 p-2 rounded bg-gray-100" value={user.positionCode?.code || ''} disabled />
            </div>

            <div>
              <label className="text-sm text-gray-700">{t('Location')}</label>
              <input className="w-full border border-gray-300 p-2 rounded bg-gray-100" value={employeeLocation} disabled />
            </div>

            <div>
              <label className="text-sm text-gray-700">{t('Line Manager')}</label>
              <input className="w-full border border-gray-300 p-2 rounded bg-gray-100" value={user.lineManager} disabled />
            </div>

            <div>
              <label className="text-sm text-gray-700">{t('Dates')}</label>
              <TailwindPeriodInput
                id={'info-support-details-dates-' + id + comment + userId + idx}
                readOnly={readonly}
                onChange={period => datesChangeHandler(period, idx)}
                value={{ from: startDate, to: endDate }}
              />
            </div>

            <div>
              <label className="text-sm text-gray-700">{t('Actual Days')}</label>
              <div className="flex items-center gap-2">
                <input
                  className="w-full border border-gray-300 rounded px-2 py-1"
                  type="number"
                  disabled={readonly}
                  onChange={value => inputChangeHandler('actualDays', value, idx)}
                  value={actualDays}
                />
                <span className="whitespace-nowrap">w/d</span>
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-700">{t('Actual Hours')}</label>
              <div className="flex items-center gap-2">
                <input
                  className="w-full border border-gray-300 rounded px-2 py-1"
                  type="number"
                  disabled={readonly}
                  onChange={value => inputChangeHandler('actualHours', value, idx)}
                  value={actualHours}
                />
                <span className="whitespace-nowrap">h</span>
              </div>
            </div>

            <div className="info-support-details__employee-detailed hzt-section">
              <label className="text-sm text-gray-700">{t('Actual Cost')}</label>
              <div className="flex items-center gap-2">
                <input
                  className="w-full border border-gray-300 rounded px-2 py-1"
                  type="number"
                  disabled={readonly || isInstructor()}
                  onChange={value => inputChangeHandler('actualCost', value, idx)}
                  value={actualCost}
                />
                <span className="whitespace-nowrap">$</span>
              </div>
            </div>

            {status.toString() !== 'Draft' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('Travel Days')}</label>
                  <DatesRangeInput
                    className="travel-days__departure"
                    dontUseRedClearIconColor
                    iconPosition="right"
                    readOnly={readonly}
                    fromValue={travelDays?.departure?.from}
                    toValue={travelDays?.departure?.to}
                    onChange={(from, to) => handleTravelDays('departure', { from, to }, idx)}
                    placeholder={t('Departure')}
                    clearable
                    icon={false}
                    allowSameEndDate
                    getEveryChange
                  />
                  <DatesRangeInput
                    className="travel-days__arrival"
                    dontUseRedClearIconColor
                    iconPosition="right"
                    readOnly={readonly}
                    fromValue={travelDays?.arrival?.from}
                    toValue={travelDays?.arrival?.to}
                    onChange={(from, to) => handleTravelDays('arrival', { from, to }, idx)}
                    placeholder={t('Arrival')}
                    clearable
                    icon={false}
                    allowSameEndDate
                    getEveryChange
                  />
                </div>
                <div className="flex items-center gap-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('No Travel')}</label>
                  <div className="flex items-center">
                    <input
                      disabled={readonly}
                      id="default-checkbox"
                      type="checkbox"
                      value=""
                      checked={noTravel}
                      className="w-4 h-4 text-black-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      onChange={data => handleNoTravel(idx, data.target.checked)}
                    />
                  </div>
                </div>
              </>
            )}

            {isAdminPocOrPlanner() && !readonly && !isHideEditButton && <Icon onClick={() => changeEmployeeHandler(idx)} name="edit" />}
          </div>
        );
      })}
      {emptyPositions}
      {showEmployeeSelectionModal && (
        <ChooseStudentsForm
          maxSelection={1}
          showSFPositionFields
          showPositionCodeFields
          onAddParticipants={onEmployeesSelected}
          alreadySelectedUserIds={(eventPositions || []).map(({ userId }) => userId)}
          onCloseUsersModal={closeModal}
        />
      )}
    </>
  );
};
export default withTranslation()(React.memo(TailwindInfoSupportDetailed));
